import process from 'process';

export const IndexPages = {
  HOME: 'home',
  WORK: 'work',
  SOCIAL: 'social',
} as const;
export type IndexPageType = (typeof IndexPages)[keyof typeof IndexPages];
export const DefaultIndexPage = IndexPages.HOME;

export const FnCoCompanyCode = ['N', 'T', 'US'];
export const FnHoldingsCompanyCode = ['HC', 'FE', 'FL'];

/**
 * 신규 '연차 신청 모달'(PORTAL-11037)을 노출하는 법인코드.
 * - A(에프앤에프) / HC(에프앤에프홀딩스) / D(에프앤에프파트너스)
 *   / FL(에프앤에프로지스틱스) / FE(에프앤에프엔터테인먼트)
 * 그 외 법인(F&CO 등)은 기존 외부 결재 링크 버튼을 노출한다. (PORTAL-11903)
 */
export const AnnualLeaveModalCompanyCode = ['A', 'HC', 'D', 'FL', 'FE'];

export const VIP_NUM = 'A199809001';
export const VIP_PASSPORT_ID = '6549ee8446c21937d00af519';

export const isDevApi =
  process.env.NEXT_PUBLIC_API_URL === 'https://portal-backend-dev.fnf.co.kr';

export const isLocal =
  process.env.NEXT_PUBLIC_ORIGIN_URL === 'http://localhost:3000';
export const isDev =
  process.env.NEXT_PUBLIC_ORIGIN_URL === 'https://portal.dev.fnf.co.kr';
export const isStg =
  process.env.NEXT_PUBLIC_ORIGIN_URL === 'https://portal.stg.fnf.co.kr';
export const isProd =
  process.env.NEXT_PUBLIC_ORIGIN_URL === 'https://portal.fnf.co.kr';

// 검증 중(PREVIEW) 유형의 노출 범위: dev/stg/로컬. prod 는 STABLE 만 본다.
export const IS_APPROVAL_V2_ENABLED = isLocal || isDev || isStg;

/**
 * 계약 품의(117·118) AI 초안 — 로컬·dev 만. 법틀 계약서 원문 다운로드 오류가 풀릴 때까지 stg·운영은 숨김(PORTAL-14185)
 */
export const IS_CONTRACT_AI_DRAFT_ENABLED = isLocal || isDev;

/**
 * 세일즈 시즌 기준 선택(PORTAL-14230)·시즌 구성 보기(PORTAL-14418) 노출.
 * 2026-10-08 부터 운영만 꺼 두었다가 2026-10-26 운영 오픈 — iOS 앱(SalesFeatureFlags.isSeasonBasisEnabled, 10-16 오픈)과 맞춘다.
 * 끄면 예전 시즌 버튼·시트(v1 목록, 상품 시즌 고정)로 돌아가고 URL 의 basis=OPER 도 무시한다. 끌 일이 생기면 이 한 줄.
 */
export const IS_SEASON_BASIS_ENABLED = true;

/**
 * 결재 목록 검색을 V2 검색 API(GET /api/v2/approval)로 호출 (PORTAL-14909) — 로컬·dev 만.
 * stg·운영과 환경 판정이 안 되는 경우는 기존 API(GET /api/v1/legacy-approval).
 */
export const IS_APPROVAL_SEARCH_V2_ENABLED = isLocal || isDev;

// '나의 결재라인' 노출: 배포 보류(폐기 아님)로 stg/prod OFF, 로컬/dev 만 노출.
// 배포 재개 시 이 조건을 넓힐 것.
export const IS_MY_APPROVAL_LINE_ENABLED = isLocal || isDev;

/**
 * 근태 취소 품의 → 취소 대상 원 품의 유형. 원 품의 유형이 여기와 다르면 취소 대상으로
 * 받지 않음. 6종은 작성 폼·저장 attributes·조회 슬롯을 한 구현이 공유하며,
 * `create?apv_type={취소양식}&doc={원 품의}` 진입으로만 열림.
 */
export const CANCEL_ORIGIN_TYPE: Readonly<Record<number, number>> = {
  226: 223, // 연장근무 신청 취소 ← (신규)연장근무 신청
  227: 224, // 연장근무 적립 사용 취소 ← (신규)연장근무 적립 사용
  232: 231, // 연차 취소 ← (신규)연차 신청
  237: 5, // 기타(휴근계) 취소 ← 기타(휴근계)
  238: 214, // 장기근속 포상휴가 취소 ← 장기근속 포상휴가 신청서
  239: 63, // 휴일근무 취소 ← 휴일근무 신청
};
export const CANCEL_APPROVAL_TYPES: number[] =
  Object.keys(CANCEL_ORIGIN_TYPE).map(Number);
/** 위 표 밖에서 함께 받는 원 품의 — 232 는 레거시 연차휴가(32)도 취소(백엔드가 대상 문서 유형으로 판정) */
export const CANCEL_EXTRA_ORIGIN_TYPES: Readonly<Record<number, number[]>> = {
  232: [32],
};

/**
 * 계약 품의 2종. 레거시부터 작성 폼·저장·조회가 완전히 같아 한 구현을 공유하며,
 * 양식 목록엔 isDisplay:false 라 법틀에서 계약을 들고 진입하는 경로로만 열림.
 */
export const CONTRACT_APPROVAL_TYPES: number[] = [
  117, // 계약 품의
  118, // 계약 연장
];
// 운영 포함 전 환경에 노출할, 검증 완료된 native 품의 유형.
const NATIVE_TYPES_STABLE: number[] = [
  ...CANCEL_APPROVAL_TYPES,
  1, // 기본품의
  3, // SAMPLE 신청
  11, // 중간관리교체
  20, // 마진변경
  31, // 인턴채용
  34, // SMS 발송
  36, // 프로모션 진행
  65, // 부자재 신청
  115, // 신규 프로젝트
  211, // F&CO 인사발령
  16, // 이벤트 진행
  37, // 바이어 할인권 — isDisplay:false 라 양식 목록에서 작성 진입 안 됨
  38, // 기타 할인권 — 37 과 같음
  64, // 쇼룸 출고 신청 (Manual) — 209 '쇼룸 출고 신청' 과 별개, isDisplay:false
  235, // AI 서비스 구독 신청 — 어드민 고정 결재자(fixedApprovers)가 결재라인 맨 끝에 잠김
  194, // Intranet(Web/App) 시스템 모니터링
  189, // 유통 시스템 접근권한 모니터링
  190, // 생산 시스템 접근권한 모니터링
  192, // 통합계정 시스템 접근권한 모니터링
  193, // 온라인 시스템 접근권한 모니터링
  191, // SAP 시스템 접근권한 모니터링
  112, // 수입결의서정산 — 협조결재자 지정 부서 1명만(submitChecks)
  132, // 세금계산서 발행요청
  178, // 역분개 요청신청
  134, // 마케팅/자가소모
  130, // 백화점 온라인 행사품의
  137, // 위탁 보육료 신청 품의
  208, // 공용 법인카드 사용 신청 — 제목에 작성자 이름(formSubject)
  220, // 파견직 채용 품의 — 신규/연장 라디오로 제목 결정(HireKindField)
  219, // 아르바이트 채용 품의 — 220 과 같음
  205, // 사전 비용 품의 — 비용구분·업체별 견적·지급 총액 + AI 견적서 업로더
  221, // F&CO 마케팅 사전 품의 — /app/ai-marketing-expense/fnco 에서 analysisId 진입 전용
  213, // ENT. 프로젝트(비용) 품의 — SAP 사용 예산 조회 + AI 견적서 업로더
  176, // H/W 보유 자산 신청
  175, // H/W 신규 자산 구매
  230, // S/W 신규 구매
  139, // 보안성 검토 요청서
  138, // 반출 예외 신청
  6, // 국내출장품의신청
  24, // F&CO 해외출장 사전품의신청 — 강제 협조결재자는 어드민 고정 협조결재자로 지정
  158, // 해외출장 사후품의신청 — 협조결재자에 F&F 회계팀·경영관리팀 필수(submitChecks)
  180, // 해외출장비 초과사용분 사유서신청
  233, // 매장 OPEN 품의 — 리포트(html) AI 업로더 + 공사비·투자 적정성 리포트
  234, // 인테리어 OPEN 비용 정산 품의 — 233 과 같은 진입·업로더, 정산 리포트
  183, // 작업사양서 — 담당자별 체크 항목 1개 이상(임시저장 포함)
  93, // 차량유지비 신청 — 임직원 정보 표를 attributes.content 로 저장
  196, // 퇴직원 — 사유·서약 HTML 은 attributes.content, 입력값은 attributes.data(레거시 CL02 JSON)
  204, // 신규입사자 법인카드 신청 — 서약 HTML 은 attributes.content, 신청 항목은 attributes 필드. 신분증·통장은 담당자 인증 후 열람
  185, // SAP 채권현황 — 브랜드·연월 SAP 조회 표를 attributes.content 로 저장, 임시저장 없음
  206, // 법인카드 한도증액 — draft-source 로 내 카드·한도(만원), 입력값은 attributes 필드. 임시저장도 상신과 같은 검사, 조회는 저장값
  207, // 법인카드 재발급 — 206 과 같은 카드 목록(번호만), 사유 코드·분실 신고 여부를 attributes 에
  ...CONTRACT_APPROVAL_TYPES,
  66, // 바닐라코 상품 출고 신청
  200, // 거래처 신규/변경 [F&F HK] — 업체소싱정보 HTML 은 attributes.content, 입력값은 attributes.data(레거시 CL02 JSON + 편집 복원용 bpForm)
  201, // 거래처 신규/변경 [F&F TW] — 200 과 같은 구현, 금액 구간·통화(TWD)만 다름
  113, // 법인인감 날인 신청 — 법인·인감 종류·법무팀 검토 여부를 표시 글자로 attributes 에. 셀렉트 변경마다 제목 덮어씀
  157, // SAP 중간 관리 수수료 정산 — 브랜드·연월로 draft-source 조회, 집계표 HTML·SAP 원문을 attributes 로. 임시저장 없음
  212, // 마케팅 비용 품의 — /app/ai-marketing-expense 에서 analysisId 진입 전용
  167, // SAP 도급비 수수료 정산 — 데이터 호출(draft-source)로 집계표, 임시저장 없음. 표 HTML·SAP 원문은 attributes.content·data
  81, // (구)근저당설정비용 지급 — 직접 입력 16항목을 attributes 에. 임시저장도 상신과 같은 검사, 조회는 저장값으로 표를 그림
  8, // (구)매장 OPEN 품의 — 대리점은 빈 표를 본문에(attributes 없음), 백화점·직영점은 draft-source 매장 「등록」 후 입력값을 attributes 에. 조회는 저장값으로 표
  188, // 수습평가서 — 사내 ID 로 대상자 조회(draft-source), 대상자 표 HTML 은 attributes.content, 사번은 targetEmployeeNumber
  215, // Sales 브랜드 권한 변경 — draft-source 로 브랜드·보유 여부, 체크 목록 HTML 은 attributes.content, 변경분은 addedBrands·removedBrands. 최종 승인 시 BE 가 권한 반영
  61, // 단체판매 진행 — draft-source 로 건 선택·「확인」, 키 4개 + 작성 시점 내역표를 attributes.content 로
  131, // 단체판매 진행(다중) — draft-source 로 여러 건 선택·「확인」, group(레거시 JSON) + 작성 시점 내역 HTML 을 attributes.content 로. 완료 후처리는 BE
  187, // SAP 예산증액 신청 — 연월로 draft-source 계정 조회, 계정·금액과 표 HTML 을 attributes 로. 임시저장 없음, 협조결재자 경영관리팀 1명
  135, // 인플루언서 PPL 정산 — 인플루언서 시스템 `Ep` 진입 전용, 리포트 표 HTML·그래프 데이터를 attributes 로. 임시저장 없음, 상태 반영(P·C·R)은 BE
  108, // 인플루언서 월마감 보고서 — 인플루언서 시스템 `Ep` 진입 전용, sequence 로 draft-source 리포트 표를 attributes.content 로. 임시저장 없음, 상태 반영은 BE
  32, // 연차휴가 — 휴가 사용현황·사용일수는 draft-source, 기간·구분은 attributes. 품의 취소는 232(231 과 공유). HR 반영은 BE 후처리
  240, // F&CO 장기근속 포상휴가 — 사용일수는 draft-source, 서약 HTML·입력값은 attributes.content·data. [1주년] 은 기간 없음. 품의 취소 없음(BE 가 240 취소 미지원)
  111, // 지출결의서 — 지급 내역(통화별)·지급 정보·거래처 정보·필수 확인을 attributes 로, 합계는 서버 계산. 협조결재자 지정 부서 1명만
  68, // 할인권 발행 — 브랜드·기간 조회 → 할인권 「확인」으로 표 확정, 임시저장도 할인권 필수
  99, // SA 전표처리 신청 — SAP `Ep` 진입 전용, 임시저장 없음, 협조결재자 지정 부서 1명만(submitChecks)
  209, // F&CO 쇼룸 출고 신청 — EAS `reqno` 진입 전용, 요청번호만 attributes 로. 회수 숨김(authorOngoingActions)
  202, // F&CO 신제품 발주 — EAS `reqno` 진입 전용, 요청번호만 attributes 로
  203, // F&CO 운영제품 재발주 — 202 와 같은 구현
  155, // SAP 담보 변경 — SAP `Ep` 진입 전용, sapKey 로 draft-source 변경 전·후 표를 attributes.content 로. 임시저장 없음, SAP 상태 반영은 BE
  154, // SAP 담보 신규 — 155 와 같은 진입·저장 방식, 표는 신규 한 열
  101, // 회계거래처 신규/변경 — SAP `Ep` 진입 전용, 묶음번호로 draft-source 거래처 표 + '생성'이면 업체소싱정보(200 부품). 임시저장 없음, 협조결재자 지정 부서 1명만. SAP 상태 반영은 BE
  216, // 생산비 마감(회계) — 마감 월로 draft-source SAP 집계표, 차이내역 내용만 입력. 저장 data 로 조회 화면을 다시 그림, SAP 상태 반영(P·C·R)은 BE
  177, // SA 신규 출장비 정산 신청 — 외부 시스템 `sa_docno`·`sa_ztype` 진입 전용, 표 HTML 을 attributes.content 로. 임시저장·편집 없음
  179, // SA 신규 출장비 사전 신청 — 177 과 같은 진입·저장, SAP 원문도 attributes.data 로(최종 승인 메일 받는 사람). 임시저장·편집 없음
  169, // SERP 재고실사 조정 — SERP `type_code`·`seq` 진입 전용, draft-source 로 표를 그려 attributes.content·data 로. EAI 상태 반영은 BE 후처리
  162, // SERP 매장 변경 — 169 와 같은 진입·저장 방식, 변경전/변경후 표(다른 값 빨간 글씨)
  161, // SERP 매장 개점 — 169 와 같은 진입·저장 방식
  171, // SERP 매장반품 요청 — 169 와 같은 진입·저장 방식, 조회 화면 「자세히 보기」는 저장 data 의 작성 시점 상세
  163, // SERP 여신유예신청 — 169 와 같은 진입·저장 방식
  181, // SERP 폐점 정산 — 169 와 같은 진입·저장 방식
  182, // SERP 중간관리자 교체 정산 — 169 와 같은 진입·저장 방식
  160, // SERP 판매가 조정 — 169 와 같은 진입·저장 방식, 상세 표 「상세 보기」 접고 펴기는 조회 화면에서도
  159, // SERP 시즌운영계획 — 169 와 같은 진입·저장 방식, 표 3개 「상세 보기」 접고 펴기(조회 화면 포함)
  210, // SERP 재고실사 조정(다중) — 169 와 같은 진입, 문서 번호 목록은 attributes.data.sequence
  174, // SERP 시즌마감 조정 — 169 와 같은 진입·저장 방식, 정산내역(매장별)은 저장 HTML 의 모달 내용
  170, // SERP AP 재고 이관 요청 — 169 와 같은 진입·저장 방식
  197, // 가격 등록 공급가 요청 — EAS `reqno` 진입 전용, 요청번호로 SAP 조회 표 HTML·원문을 attributes.content·data 로
  198, // 가격 등록 할인율 요청 — 197 과 같은 구조, 표 열·제목·표 아래 안내만 다름
  199, // 가격 등록 판매가 요청 — 197 과 같은 구조, 표 열·제목만 다름
  83, // (구)마케팅 건별 진행 — draft-source 로 진행 대기 건 선택·「확인」, 키 4개 + 작성 시점 표·견적 상세 HTML 을 attributes.content 로. 새로 고른 건은 최상위 marketingCase 로 비용 건 연결(BE)
];
// 로컬/dev/stg 에서만 검증 중인 native 품의 유형. 운영 전환 시 STABLE 로 이동.
const NATIVE_TYPES_PREVIEW: number[] = [
  217, // 외담대 발행 — V2 신규 양식. 구매카드 발급일로 draft-source SAP 발행내역, 소계는 화면 계산. SAP 진입은 `issueDate`(YYYYMMDD). 저장 attributes 로 조회 화면을 다시 그림, SAP 상태 반영(P·C·R)은 BE
  156, // GERP CREDIT NOTE — draft-source 로 내 미상신 건 여러 개 선택, 카드 HTML 은 attributes.content. 임시저장 없음, DETAIL 은 OZ 리포트(locale=en_US), 조회 화면 부분반려(partial-reject), GERP 상태 반영은 BE
  136, // GERP Sales Contract — draft-source 로 내 미상신 건 여러 개 선택, 계약 카드 HTML 은 attributes.content. 임시저장 없음, Print 는 OZ 리포트, GERP 상태 반영은 BE
  128, // GERP STYLE MASTER 가격 변경 — draft-source 로 내 미상신 건 여러 개 선택, 표 HTML 은 attributes.content. 임시저장 없음, GERP 상태 반영·부분 반려는 BE
  120, // GERP 생산거래처 신규/변경 — draft-source 로 내 미상신 건 하나 선택·불러오기, 본문 HTML 은 attributes.content. 임시저장 없음, GERP 상태 반영은 BE
  123, // GERP PLM — PLM 시스템(CENTRIC·NEW_PLM) 선택 후 draft-source 로 내 미상신 품번 여러 건 선택, 카드 HTML 은 attributes.content. 임시저장 없음, GERP 상태 반영은 BE
  122, // GERP PR(제상품) — draft-source 로 내 미상신 PR 여러 건 선택, 카드 HTML 은 attributes.content. 조회 화면 부분반려(partial-reject), GERP 상태 반영은 BE
  129, // GERP PO(합의납기일/생산처) — 122 와 같은 코드, 카드에 변경 전후 합의납기·생산처. 부분반려·GERP 상태 반영은 BE
  124, // GERP 생산비 마감 — draft-source(마감 월)로 내 미상신 건 하나 선택·「확인」, 본문 HTML 은 attributes.content. 임시저장 있음, 행 상세보기는 작성=draft-source·조회=gerp-detail, GERP 상태 반영은 BE
  121, // GERP 원가 견적서 — draft-source 로 내 미상신 견적서 여러 건 선택(체크 때 발주서 전송·예정원가 생성 확인), 카드 HTML 은 attributes.content. 조회 화면 부분반려(partial-reject), GERP 상태 반영은 BE
  166, // GERP CLAIM NOTE — 수입/수출·클레임 유형 고른 뒤 draft-source 로 내 미상신 건 여러 개 선택, 카드 HTML·claimType 은 attributes. 임시저장 있음, Print 는 OZ 리포트, GERP 상태 반영은 BE
  125, // GERP 저장부자재 발주 — draft-source 로 내 미상신 건 하나 선택·「확인」, 본문 HTML 은 attributes.content. 임시저장 있음, GERP 상태 반영·완료 메일은 BE
  184, // GERP 저장품 추가발주 — draft-source 로 내 미상신 건 여러 개 선택, 발주 카드 HTML 은 attributes.content. 임시저장 없음, GERP 상태 반영은 BE
  186, // GERP 클레임 마감 — draft-source 로 내 미상신 건 여러 개 선택, 카드 HTML 은 attributes.content. 임시저장 있음, 카드 상세보기는 작성=draft-source·조회=gerp-detail, GERP 상태 반영은 BE
  127, // GERP 협력사 평가 — draft-source 로 내 미상신 건 하나 선택·「확인」, 평가 표 HTML 은 attributes.content. 임시저장 있음, GERP 상태 반영은 BE
];

// 자체구현(native) 라우팅 대상. 운영=STABLE, 로컬/dev/stg=STABLE+PREVIEW.
export const NATIVE_APPROVAL_TYPES: number[] = IS_APPROVAL_V2_ENABLED
  ? [...NATIVE_TYPES_STABLE, ...NATIVE_TYPES_PREVIEW]
  : NATIVE_TYPES_STABLE;

// 조회만 native 로 여는 유형(작성 미포함). 근태 품의는 작성이 포탈 전용 모달
// (CreateApprovalModal/attendanceApproval.ts)이라 NATIVE_APPROVAL_TYPES 에 넣으면
// 작성 라우팅까지 V2 작성폼으로 새어 나감 — 그래서 목록을 따로 둠.
// 운영 포함 전 환경에 노출할, 검증 완료된 조회전용 유형.
const VIEW_ONLY_TYPES_STABLE: number[] = [
  222, // (신규)근무시간 변경
  223, // (신규)연장근무 신청
  224, // (신규)연장근무 적립 사용
  231, // (신규)연차 신청
];
// 로컬/dev/stg 에서만 검증 중인 조회전용 유형. 운영 전환 시 VIEW_ONLY_TYPES_STABLE 로 이동.
// 취소 품의는 작성까지 V2 라 NATIVE 쪽(CANCEL_APPROVAL_TYPES).
const VIEW_ONLY_TYPES_PREVIEW: number[] = [];

// 조회 화면(뷰어·모달·다이얼로그·조회 URL) 판정 전용. 작성·편집 라우팅엔 NATIVE_APPROVAL_TYPES 를 쓸 것.
export const NATIVE_VIEW_APPROVAL_TYPES: number[] = [
  ...NATIVE_APPROVAL_TYPES,
  ...(IS_APPROVAL_V2_ENABLED
    ? [...VIEW_ONLY_TYPES_STABLE, ...VIEW_ONLY_TYPES_PREVIEW]
    : VIEW_ONLY_TYPES_STABLE),
];

export const twoWeek = 24 * 60 * 60 * 14;
